import { Tag } from "primereact/tag";

import { ExportTableDialog, HubTable, MonthRangePicker, TableSettingsDialog } from "@/ds";
import type { ProfissionaisSortField } from "@/modules/recursos";
import { HubTableCartao } from "@/shared/components/hub-table-cartao";
import { ListaToolbar } from "@/shared/components/lista-toolbar";
import { UserInfoCard } from "@/shared/components/user-info-card";

import {
  PROFISSIONAIS_COLUMNS,
  PROFISSIONAIS_EMPTY_NOUN,
  PROFISSIONAIS_EMPTY_PLURAL,
  PROFISSIONAIS_EXPORT_LIST_NAME,
  PROFISSIONAIS_LOADING_LABEL,
  PROFISSIONAIS_ROWS_PER_PAGE_LABEL,
  PROFISSIONAIS_SEARCH_PLACEHOLDER,
  PROFISSIONAIS_TABLE_LABEL,
  UNAVAILABLE_LABEL,
} from "./profissionais.constants";
import styles from "./profissionais.module.scss";
import type {
  ProfissionaisColumnKey,
  ProfissionaisScreenViewModel,
  ProfissionalLinha,
} from "./types-profissionais";

function renderProfissional(linha: ProfissionalLinha) {
  return (
    <div className={styles.profissionalCell} data-testid={linha.rowTestId}>
      <div className={styles.profissionalName}>
        <UserInfoCard
          {...linha.cartao}
          nome={linha.nome}
          email={linha.email}
          telefone={linha.telefone}
        >
          {linha.nome}
        </UserInfoCard>
        {linha.indisponivel ? (
          <Tag
            className="p-tag-xs"
            severity="danger"
            icon="pi pi-exclamation-triangle"
            value={UNAVAILABLE_LABEL}
            data-testid={linha.unavailableTestId}
          />
        ) : null}
      </div>
      <span className={styles.profissionalEmail}>{linha.email}</span>
    </div>
  );
}

/** RN05/RN06 — só o intervalo início → fim; o motivo nunca é exibido. */
function renderIndisponibilidade(linha: ProfissionalLinha) {
  return linha.periodos.length > 0 ? (
    <span className={styles.periodoList}>
      {linha.periodos.map((periodo) => (
        <span key={periodo.key}>{periodo.texto}</span>
      ))}
    </span>
  ) : null;
}

function renderCell(key: ProfissionaisColumnKey, linha: ProfissionalLinha) {
  switch (key) {
    case "nome":
      return renderProfissional(linha);
    case "indisponibilidade":
      return renderIndisponibilidade(linha);
    default:
      return linha[key];
  }
}

/**
 * Profissionais (HU 1761, Figma 5212:130632): consulta somente leitura, sem cadastro nem edição (RN02).
 * A foto do profissional só aparece no hover do nome.
 */
export function ProfissionaisScreenView({
  isMobile,
  search,
  buscar,
  periodo,
  escolherPeriodo,
  filterColumns,
  filterValue,
  aplicarFiltros,
  filtrosSalvos,
  linhas,
  total,
  status,
  errorMessage,
  table,
  settings,
  hasSearch,
  hasFilters,
  limparCriterios,
  tentarNovamente,
  exportView,
  avisarConfiguracoesAplicadas,
}: ProfissionaisScreenViewModel) {
  return (
    <div className={styles.container} data-testid="profissionais-screen">
      <ListaToolbar
        testIdPrefix="profissionais"
        isMobile={isMobile}
        search={search}
        onSearchChange={buscar}
        searchPlaceholder={PROFISSIONAIS_SEARCH_PLACEHOLDER}
        wideSearch
        quickFilters={
          <MonthRangePicker
            value={periodo}
            onChange={escolherPeriodo}
            testId="profissionais-periodo"
          />
        }
        filterDefinitions={filterColumns}
        filterValue={filterValue}
        onFilterChange={aplicarFiltros}
        {...filtrosSalvos}
        onExport={exportView.open}
        onOpenSettings={settings.dialog.open}
      />

      {exportView.isOpen ? (
        <ExportTableDialog
          listName={PROFISSIONAIS_EXPORT_LIST_NAME}
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      <TableSettingsDialog
        dialog={settings.dialog}
        testId="profissionais-table-settings"
        onApplied={avisarConfiguracoesAplicadas}
      />

      <div className={styles.content}>
        <HubTableCartao<ProfissionalLinha, ProfissionaisColumnKey, ProfissionaisSortField>
          value={linhas}
          totalRecords={total}
          table={table}
          settings={settings}
          status={status}
          errorMessage={errorMessage}
          loadingLabel={PROFISSIONAIS_LOADING_LABEL}
          emptyNoun={PROFISSIONAIS_EMPTY_NOUN}
          emptyPlural={PROFISSIONAIS_EMPTY_PLURAL}
          hasSearch={hasSearch}
          hasFilters={hasFilters}
          onClear={limparCriterios}
          onRetry={tentarNovamente}
          dataTableProps={{
            scrollable: true,
            scrollHeight: "flex",
            tableStyle: { minWidth: "48rem" },
            "aria-label": PROFISSIONAIS_TABLE_LABEL,
          }}
          rowsPerPageLabel={PROFISSIONAIS_ROWS_PER_PAGE_LABEL}
          testId="profissionais"
        >
          {PROFISSIONAIS_COLUMNS.map((column) => (
            <HubTable.Column<ProfissionalLinha, ProfissionaisColumnKey>
              key={column.key}
              field={column.key}
              header={column.header}
              sortable={column.sortable}
              style={column.style}
              body={(linha) => renderCell(column.key, linha)}
            />
          ))}
        </HubTableCartao>
      </div>
    </div>
  );
}
